import { useState, useEffect } from 'react';
import { getPaymentPreparation, savePaymentPreparation, getCashSummary, getNextDeliveryDate, getSuppliers, getWeeklyPaymentSuppliers, getNextWeekCreditSummary, getMonthlyCashCreditSummary } from '../lib/api';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Printer, Plus, Trash2, Check, Calendar, AlertCircle, Banknote, Wallet, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

const DEFAULT_FUND = 50000;

export const PaymentPreparation = () => {
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [cashSummary, setCashSummary] = useState({ unpaid_items: [], unpaid_total: 0, paid_items: [], paid_total: 0 });
  const [todayPrep, setTodayPrep] = useState({ prepared_amount: DEFAULT_FUND });
  const [editData, setEditData] = useState({ irregular_items: [], weekly_items: [], tomorrow_fund: 0 });
  const [newIrregularName, setNewIrregularName] = useState('');
  const [newIrregularAmount, setNewIrregularAmount] = useState('');
  const [newIrregularSupplierId, setNewIrregularSupplierId] = useState('');
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [weeklySuppliers, setWeeklySuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nextDeliveryDate, setNextDeliveryDate] = useState('');
  const [nextWeekCredit, setNextWeekCredit] = useState({ start_date: '', end_date: '', items: [], total: 0 });
  const [monthlyCashCredit, setMonthlyCashCredit] = useState({ start_date: '', end_date: '', is_target_date: false, items: [], total: 0 });

  const nextDeliveryDisplay = nextDeliveryDate ? format(new Date(nextDeliveryDate), 'M月d日(E)', { locale: ja }) : '...';
  const todayDisplay = format(new Date(selectedDate), 'M月d日(E)', { locale: ja });

  useEffect(() => { fetchData(); }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const nextDateRes = await getNextDeliveryDate(selectedDate);
      const nextDate = nextDateRes.data.next_delivery_date;
      setNextDeliveryDate(nextDate);

      const [cashRes, todayPrepRes, nextPrepRes, suppRes, weeklySuppRes, nextWeekRes, monthlyRes] = await Promise.all([
        getCashSummary(selectedDate),
        getPaymentPreparation(selectedDate),
        getPaymentPreparation(nextDate),
        getSuppliers(),
        getWeeklyPaymentSuppliers(),
        getNextWeekCreditSummary(selectedDate),
        getMonthlyCashCreditSummary(selectedDate),
      ]);
      setCashSummary(cashRes.data);
      setAllSuppliers(suppRes.data);
      setWeeklySuppliers(weeklySuppRes.data);
      setTodayPrep({ prepared_amount: todayPrepRes.data.prepared_amount || DEFAULT_FUND });
      const nextWeekData = nextWeekRes.data || { start_date: '', end_date: '', items: [], total: 0 };
      setNextWeekCredit(nextWeekData);
      setMonthlyCashCredit(monthlyRes.data || { start_date: '', end_date: '', is_target_date: false, items: [], total: 0 });
      const nextWeekBySupplier = Object.fromEntries((nextWeekData.items || []).map(i => [i.supplier_id, i]));

      // 週払いデータ: 金額は常に最新の自動集計値を使用、paidフラグだけ保存済みから引き継ぐ
      const savedWeekly = todayPrepRes.data.weekly_items || [];
      const weeklyItems = weeklySuppRes.data.map(s => {
        const saved = savedWeekly.find(w => w.supplier_id === s.id);
        const auto = nextWeekBySupplier[s.id]?.amount || 0;
        return {
          supplier_id: s.id,
          supplier_name: s.name,
          amount: auto,                 // 常に最新の自動集計値
          paid: saved ? !!saved.paid : false,  // paidチェックのみ保存済みから引き継ぐ
          auto_amount: auto
        };
      });

      setEditData({
        irregular_items: todayPrepRes.data.irregular_items || [],
        weekly_items: weeklyItems,
        tomorrow_fund: nextPrepRes.data.prepared_amount || 0,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const addIrregular = () => {
    if (!newIrregularName.trim()) { toast.error('項目名を入力してください'); return; }
    const amount = parseFloat(newIrregularAmount) || 0;
    if (amount <= 0) { toast.error('金額を入力してください'); return; }
    const supplier = allSuppliers.find(s => s.id === newIrregularSupplierId);
    setEditData(prev => ({
      ...prev,
      irregular_items: [...prev.irregular_items, {
        id: `irr-${Date.now()}`,
        name: newIrregularName,
        amount,
        supplier_id: newIrregularSupplierId || '',
        supplier_name: supplier ? supplier.name : 'その他',
      }]
    }));
    setNewIrregularName('');
    setNewIrregularAmount('');
    setNewIrregularSupplierId('');
  };

  const removeIrregular = (id) => {
    setEditData(prev => ({
      ...prev,
      irregular_items: prev.irregular_items.filter(i => i.id !== id)
    }));
  };

  const handleSave = async (overrideData) => {
    setSaving(true);
    try {
      // 保存ボタンのクリックイベントが第1引数に来た場合は無視
      const isOverride = overrideData && typeof overrideData === 'object' && Array.isArray(overrideData.weekly_items);
      const dataToSave = isOverride ? overrideData : editData;
      await savePaymentPreparation({
        date: selectedDate,
        prepared_amount: todayPrep.prepared_amount,
        irregular_items: dataToSave.irregular_items,
        weekly_items: dataToSave.weekly_items
          .filter(w => w.amount > 0 || w.paid)
          .map(w => ({ supplier_id: w.supplier_id, supplier_name: w.supplier_name, amount: w.amount || 0, paid: !!w.paid })),
      });
      if (!isOverride) {
        await savePaymentPreparation({
          date: nextDeliveryDate,
          prepared_amount: dataToSave.tomorrow_fund,
          irregular_items: [],
          weekly_items: [],
        });
      }
      if (!isOverride) toast.success('保存しました');
    } catch (error) {
      console.error('save error:', error);
      toast.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // 週払い行の「支払い済み」チェック切替
  const toggleWeeklyPaid = async (supplierId) => {
    const newItems = editData.weekly_items.map(w =>
      w.supplier_id === supplierId ? { ...w, paid: !w.paid } : w
    );
    const newData = { ...editData, weekly_items: newItems };
    setEditData(newData);
    // 即座に保存（次回開いた時もチェック状態保持）
    try {
      await savePaymentPreparation({
        date: selectedDate,
        prepared_amount: todayPrep.prepared_amount,
        irregular_items: newData.irregular_items,
        weekly_items: newItems
          .filter(w => w.amount > 0 || w.paid)
          .map(w => ({ supplier_id: w.supplier_id, supplier_name: w.supplier_name, amount: w.amount || 0, paid: !!w.paid })),
      });
    } catch (e) {
      toast.error('支払い済みチェックの保存に失敗しました');
    }
  };

  const todayFund = todayPrep.prepared_amount;
  const irregularTotal = editData.irregular_items.reduce((sum, i) => sum + (i.amount || 0), 0);
  const weeklyTotal = editData.weekly_items.filter(w => !w.paid).reduce((sum, i) => sum + (i.amount || 0), 0);
  const monthlyTotal = (monthlyCashCredit.is_target_date ? (monthlyCashCredit.total || 0) : 0);
  const tomorrowRequired = cashSummary.unpaid_total + irregularTotal + weeklyTotal + monthlyTotal;
  const pettyCashRemaining = todayFund - cashSummary.paid_total;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="payment-prep-page">
      <div className="flex items-center justify-between no-print">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">支払い準備</h1>
          <p className="text-slate-500 mt-1">{todayDisplay}の実績と明日の準備</p>
        </div>
        <div className="flex items-center gap-3">
          <Label className="flex items-center gap-2"><Calendar className="h-4 w-4" />日付:</Label>
          <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-40" data-testid="prep-date-input" />
          <Button variant="outline" onClick={() => window.print()} data-testid="print-prep-btn">
            <Printer className="h-4 w-4 mr-2" />印刷
          </Button>
          <Button onClick={() => handleSave()} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            <Check className="h-4 w-4 mr-2" />{saving ? '保存中...' : '保存'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ========== 左側: 本日の実績 ========== */}
        <div className="space-y-4 lg:order-1">
          <h2 className="text-lg font-bold text-slate-700 flex items-center gap-2">
            <Banknote className="h-5 w-5" />
            {todayDisplay}の実績
          </h2>

          {/* 本日の準備金 */}
          <Card className="border-blue-200">
            <CardContent className="pt-5">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-blue-700">本日の準備金</span>
                <span className="font-mono font-bold text-xl text-blue-700">¥{todayFund.toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>

          {/* 本日の現金支払い実績 */}
          <Card className="border-green-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-green-700">現金支払い実績（支払い済み）</CardTitle>
            </CardHeader>
            <CardContent>
              {cashSummary.paid_items.length === 0 ? (
                <p className="text-sm text-slate-400 py-3 text-center">本日の現金支払い実績はありません</p>
              ) : (
                <div className="space-y-2">
                  {cashSummary.paid_items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-green-50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{item.supplier_name}</p>
                        <p className="text-xs text-slate-500">{item.payment_method}</p>
                      </div>
                      <span className="font-mono font-bold text-green-700">-¥{item.amount.toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-2 border-t border-green-200">
                    <span className="text-sm font-medium text-green-700">支払い済み合計</span>
                    <span className="font-mono font-bold text-lg text-green-700">-¥{cashSummary.paid_total.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* 小口袋残金 */}
          <Card className="border-slate-800 bg-slate-900 text-white">
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-400">準備金</span>
                  <span className="font-mono">¥{todayFund.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-400">支払い済み</span>
                  <span className="font-mono">-¥{cashSummary.paid_total.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-slate-700">
                  <span className="text-lg font-bold">小口袋 残金</span>
                  <span className={`font-mono font-bold text-2xl ${pettyCashRemaining >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    ¥{pettyCashRemaining.toLocaleString()}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ========== 右側: 明日の準備 ========== */}
        <div className="space-y-4 lg:order-2">
          <h2 className="text-lg font-bold text-slate-700 flex items-center gap-2">
            <ArrowRight className="h-5 w-5" />
            {nextDeliveryDisplay}の準備
          </h2>

          {/* 明日の支払い予定（現金未払い分） */}
          <Card className="border-red-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-red-700">現金支払い予定（未払い分）</CardTitle>
            </CardHeader>
            <CardContent>
              {cashSummary.unpaid_items.length === 0 ? (
                <p className="text-sm text-slate-400 py-3 text-center">未払いの現金支払いはありません</p>
              ) : (
                <div className="space-y-2">
                  {cashSummary.unpaid_items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-red-50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{item.supplier_name}</p>
                        <p className="text-xs text-slate-500">{item.payment_method}</p>
                      </div>
                      <span className="font-mono font-bold text-red-700">¥{item.amount.toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-2 border-t border-red-200">
                    <span className="text-sm font-medium text-red-700">現金未払い小計</span>
                    <span className="font-mono font-bold text-lg text-red-700">¥{cashSummary.unpaid_total.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* 掛け支払い予定 */}
          {(cashSummary.credit_unpaid_items || []).length > 0 && (
            <Card className="border-orange-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-orange-700">掛け支払い予定</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(cashSummary.credit_unpaid_items || []).map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-orange-50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{item.supplier_name}</p>
                        <p className="text-xs text-slate-500">{item.payment_method}</p>
                      </div>
                      <span className="font-mono font-bold text-orange-700">¥{item.amount.toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-2 border-t border-orange-200">
                    <span className="text-sm font-medium text-orange-700">掛け未払い小計</span>
                    <span className="font-mono font-bold text-lg text-orange-700">¥{(cashSummary.credit_unpaid_total || 0).toLocaleString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          <p className="text-xs text-red-600 font-medium px-1">※納品書とお金を一緒に個別で入れて準備お願いします。</p>

          {/* イレギュラー支払い（支払先選択付き） */}
          <Card className="border-amber-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-amber-700">イレギュラー支払い</CardTitle>
            </CardHeader>
            <CardContent>
              {editData.irregular_items.length > 0 && (
                <div className="space-y-2 mb-3">
                  {editData.irregular_items.map(item => (
                    <div key={item.id} className="flex justify-between items-center p-2 bg-amber-50 rounded-lg">
                      <div>
                        <span className="text-sm font-medium">{item.name}</span>
                        <span className="text-xs text-slate-500 ml-2">({item.supplier_name || 'その他'})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-amber-700">¥{item.amount.toLocaleString()}</span>
                        <button onClick={() => removeIrregular(item.id)} className="text-red-400 hover:text-red-600 no-print"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-2 border-t border-amber-200">
                    <span className="text-sm font-medium text-amber-700">イレギュラー小計</span>
                    <span className="font-mono font-bold text-amber-700">¥{irregularTotal.toLocaleString()}</span>
                  </div>
                </div>
              )}
              <div className="flex gap-2 no-print flex-wrap">
                <select
                  value={newIrregularSupplierId}
                  onChange={(e) => setNewIrregularSupplierId(e.target.value)}
                  className="border rounded-md px-2 py-1.5 text-sm bg-white w-36"
                >
                  <option value="">その他</option>
                  {allSuppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <Input value={newIrregularName} onChange={(e) => setNewIrregularName(e.target.value)} placeholder="項目名" className="flex-1 min-w-[100px]" />
                <Input type="number" value={newIrregularAmount} onChange={(e) => setNewIrregularAmount(e.target.value)} placeholder="金額" className="w-28 text-right" />
                <Button variant="outline" size="icon" onClick={addIrregular}><Plus className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>

          {/* 週払い */}
          <Card className="border-purple-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-purple-700 flex items-center justify-between gap-2">
                <span>次回の週払い（翌週掛け）</span>
                {nextWeekCredit.start_date && nextWeekCredit.end_date && (
                  <span className="text-xs font-normal text-purple-500">
                    集計期間: {format(new Date(nextWeekCredit.start_date), 'M/d', { locale: ja })}〜{format(new Date(nextWeekCredit.end_date), 'M/d', { locale: ja })} 自動集計
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editData.weekly_items.length === 0 ? (
                <p className="text-sm text-slate-400 py-3 text-center">週払い対象の仕入れ先がありません（仕入れ先マスタの支払方法に「翌週掛け」を設定してください）</p>
              ) : (
                <div className="space-y-2">
                  {editData.weekly_items.filter(w => !w.paid).length === 0 ? (
                    <p className="text-sm text-emerald-600 py-3 text-center font-medium">✓ すべて支払い済み</p>
                  ) : editData.weekly_items.filter(w => !w.paid).map((item) => {
                    const idx = editData.weekly_items.findIndex(w => w.supplier_id === item.supplier_id);
                    return (
                      <div key={item.supplier_id} className="flex justify-between items-center p-2 bg-purple-50 rounded-lg gap-3">
                        <div className="flex items-center gap-2 flex-1 no-print">
                          <Checkbox
                            checked={!!item.paid}
                            onCheckedChange={() => toggleWeeklyPaid(item.supplier_id)}
                            data-testid={`weekly-paid-${item.supplier_id}`}
                          />
                          <span className="text-sm font-medium">{item.supplier_name}</span>
                        </div>
                        <span className="text-sm font-medium flex-1 print-only" style={{ display: 'none' }}>{item.supplier_name}</span>
                        <div className="flex items-center gap-1 no-print">
                          <span className="text-xs text-slate-500">¥</span>
                          <Input
                            type="number"
                            value={item.amount || ''}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              setEditData(prev => ({
                                ...prev,
                                weekly_items: prev.weekly_items.map((w, i) => i === idx ? { ...w, amount: val } : w)
                              }));
                            }}
                            placeholder="金額"
                            className="w-28 text-right"
                          />
                        </div>
                        <span className="font-mono font-bold text-purple-700 print-only" style={{ display: 'none' }}>
                          ¥{(item.amount || 0).toLocaleString()}
                        </span>
                      </div>
                    );
                  })}
                  {weeklyTotal > 0 && (
                    <div className="flex justify-between items-center pt-2 border-t border-purple-200">
                      <span className="text-sm font-medium text-purple-700">週払い小計</span>
                      <span className="font-mono font-bold text-purple-700">¥{weeklyTotal.toLocaleString()}</span>
                    </div>
                  )}
                  {editData.weekly_items.filter(w => w.paid).length > 0 && (
                    <details className="pt-2 border-t border-purple-100 no-print" open>
                      <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">支払い済み: {editData.weekly_items.filter(w => w.paid).length}件（クリックで折りたたみ／取り消し可能）</summary>
                      <div className="mt-2 space-y-1">
                        {editData.weekly_items.filter(w => w.paid).map((item) => (
                          <div key={item.supplier_id} className="flex justify-between items-center px-2 py-1 text-xs text-slate-500" data-testid={`weekly-paid-row-${item.supplier_id}`}>
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={true}
                                onCheckedChange={() => toggleWeeklyPaid(item.supplier_id)}
                              />
                              <span className="line-through">{item.supplier_name}</span>
                            </div>
                            <span className="font-mono line-through">¥{(item.amount || 0).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* 月掛け現金 - 月末最終営業日の前日にのみ表示 */}
          {monthlyCashCredit.is_target_date && monthlyCashCredit.items.length > 0 && (
            <Card className="border-rose-300 ring-2 ring-rose-100">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-rose-700 flex items-center justify-between gap-2">
                  <span>月掛け現金（月末最終営業日支払い）</span>
                  {monthlyCashCredit.start_date && monthlyCashCredit.end_date && (
                    <span className="text-xs font-normal text-rose-500">
                      集計期間: {format(new Date(monthlyCashCredit.start_date), 'M/d', { locale: ja })}〜{format(new Date(monthlyCashCredit.end_date), 'M/d', { locale: ja })}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {monthlyCashCredit.items.map((item) => (
                    <div key={item.supplier_id} className="flex justify-between items-center p-2 bg-rose-50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{item.supplier_name}</p>
                        <p className="text-xs text-slate-500">{item.payment_method}</p>
                      </div>
                      <span className="font-mono font-bold text-rose-700">¥{item.amount.toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-2 border-t border-rose-200">
                    <span className="text-sm font-medium text-rose-700">月掛け現金小計</span>
                    <span className="font-mono font-bold text-lg text-rose-700">¥{monthlyTotal.toLocaleString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* 最低必要金額 + 準備金額 */}
          <Card className="border-slate-800 bg-slate-900 text-white">
            <CardContent className="pt-6">
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-400">支払い予定</span>
                  <span className="font-mono">¥{cashSummary.unpaid_total.toLocaleString()}</span>
                </div>
                {irregularTotal > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-400">イレギュラー</span>
                    <span className="font-mono">¥{irregularTotal.toLocaleString()}</span>
                  </div>
                )}
                {weeklyTotal > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-400">週払い</span>
                    <span className="font-mono">¥{weeklyTotal.toLocaleString()}</span>
                  </div>
                )}
                {monthlyTotal > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-400">月掛け現金</span>
                    <span className="font-mono">¥{monthlyTotal.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-2 border-t border-slate-700">
                  <span className="font-medium">最低必要金額</span>
                  <span className="font-mono font-bold text-xl text-red-400">¥{tomorrowRequired.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-slate-700">
                  <span className="font-medium">{nextDeliveryDisplay}の準備金額</span>
                  <div className="flex items-center gap-2 no-print">
                    <span className="text-slate-400">¥</span>
                    <Input
                      type="number"
                      value={editData.tomorrow_fund || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, tomorrow_fund: parseFloat(e.target.value) || 0 }))}
                      className="w-36 text-right text-lg bg-slate-800 border-slate-600 text-white"
                      data-testid="tomorrow-fund-input"
                    />
                  </div>
                  <span className="font-mono font-bold text-xl text-green-400 print-only" style={{ display: 'none' }}>¥{editData.tomorrow_fund.toLocaleString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* 印刷用CSS */}
      <style>{`
        @media print {
          aside, header, nav, [class*="sidebar"], [class*="Sidebar"] {
            display: none !important; width: 0 !important; height: 0 !important;
          }
          body * { visibility: hidden; }
          [data-testid="payment-prep-page"], [data-testid="payment-prep-page"] * { visibility: visible; }
          [data-testid="payment-prep-page"] { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          body { font-size: 8pt; margin: 0; padding: 0; }
          [data-testid="payment-prep-page"] { font-size: 8pt; }
          [data-testid="payment-prep-page"] .space-y-6 > * { margin-top: 4px !important; }
          [data-testid="payment-prep-page"] .space-y-4 > * { margin-top: 3px !important; }
          [data-testid="payment-prep-page"] .space-y-3 > * { margin-top: 2px !important; }
          [data-testid="payment-prep-page"] .space-y-2 > * { margin-top: 1px !important; }
          [data-testid="payment-prep-page"] .pt-6 { padding-top: 8px !important; }
          [data-testid="payment-prep-page"] .pt-5 { padding-top: 6px !important; }
          [data-testid="payment-prep-page"] .pb-3 { padding-bottom: 2px !important; }
          [data-testid="payment-prep-page"] .p-2 { padding: 2px 4px !important; }
          [data-testid="payment-prep-page"] .gap-6 { gap: 10px !important; }
          input { border: none !important; background: transparent !important; font-size: 10pt !important; }
        }
        @page { size: A4 landscape; margin: 8mm; }
      `}</style>
    </div>
  );
};
